import {
  addMilliseconds,
  intlFormat,
  isValid,
  parseISO,
  startOfISOWeek,
} from "date-fns"

const millisecondsInDay = 24 * 60 * 60 * 1000
const guildLocale = "en-GB"
const guildTimeZone = "Europe/Berlin"

const formatUtcDate = (value: Date): string => {
  const localized = intlFormat(
    value,
    {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      timeZone: guildTimeZone,
    },
    { locale: guildLocale },
  )

  const [day, month, year] = localized.split("/")
  if (!year || !month || !day) {
    return ""
  }

  return `${year}-${month}-${day}`
}

const parseDateLike = (value: Date | string): Date | null => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }

  const parsedIso = parseISO(value)
  if (isValid(parsedIso)) {
    return parsedIso
  }

  const fallbackDate = new Date(value)
  return Number.isNaN(fallbackDate.getTime()) ? null : fallbackDate
}

const addUtcDays = (value: Date, days: number): Date => {
  return addMilliseconds(value, days * millisecondsInDay)
}

const toMondayFromWeekStart = (weekStartAt: Date | string): string | null => {
  const parsed = parseDateLike(weekStartAt)
  if (!parsed) {
    return null
  }

  const utcDate = new Date(
    Date.UTC(
      parsed.getUTCFullYear(),
      parsed.getUTCMonth(),
      parsed.getUTCDate(),
      0,
      0,
      0,
      0,
    ),
  )

  const utcWeekday = utcDate.getUTCDay()
  if (utcWeekday === 0) {
    return formatUtcDate(addUtcDays(utcDate, 1))
  }

  return formatUtcDate(addUtcDays(utcDate, -(utcWeekday - 1)))
}

const parseIsoDateOnly = (value: string): Date => {
  return parseISO(`${value}T00:00:00.000Z`)
}

const addDaysToIsoDate = (isoDate: string, days: number): string => {
  const base = parseIsoDateOnly(isoDate)
  return formatUtcDate(addUtcDays(base, days))
}

export const getRosterWeekendDatesForWeek = (input: {
  weekStartAt: Date | string
}) => {
  const monday = toMondayFromWeekStart(input.weekStartAt)

  if (!monday) {
    return {
      monday: "",
      saturday: "",
      sunday: "",
    }
  }

  return {
    monday,
    saturday: addDaysToIsoDate(monday, 5),
    sunday: addDaysToIsoDate(monday, 6),
  }
}

const monthFormatter = new Intl.DateTimeFormat("en-GB", {
  month: "short",
  timeZone: guildTimeZone,
})

const dayFormatter = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  timeZone: guildTimeZone,
})

const yearFormatter = new Intl.DateTimeFormat("en-GB", {
  year: "numeric",
  timeZone: guildTimeZone,
})

const formatShortDate = (
  isoDate: string,
): { month: string; day: number; year: number } => {
  const date = parseIsoDateOnly(isoDate)
  return {
    month: monthFormatter.format(date),
    day: Number.parseInt(dayFormatter.format(date), 10),
    year: Number.parseInt(yearFormatter.format(date), 10),
  }
}

export const formatWeekendRange = (input: {
  weekStartAt: Date | string
}): string => {
  const { saturday, sunday } = getRosterWeekendDatesForWeek(input)

  if (!saturday || !sunday) {
    return ""
  }

  const sat = formatShortDate(saturday)
  const sun = formatShortDate(sunday)

  if (sat.month === sun.month && sat.year === sun.year) {
    return `${sat.month} ${sat.day}–${sun.day}`
  }

  if (sat.year === sun.year) {
    return `${sat.month} ${sat.day}–${sun.month} ${sun.day}`
  }

  return `${sat.month} ${sat.day}, ${sat.year}–${sun.month} ${sun.day}, ${sun.year}`
}

export const getCurrentCalendarWeekend = () => {
  const now = new Date()
  const monday = formatUtcDate(startOfISOWeek(now))

  if (!monday) {
    return { saturday: "", sunday: "" }
  }

  return {
    saturday: addDaysToIsoDate(monday, 5),
    sunday: addDaysToIsoDate(monday, 6),
  }
}
